import { useState, useEffect } from "react";
import { SaveMaterial, uploadMaterialAttachment } from "./MaterialService";
import { GetTypeCatalog } from "./TypeCatalogService";
import { formatDateForMySQL } from "../../../components/formatDate";
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useInventoryPermission } from "../permissions/InventoryPermissions";
import { getUnits, saveUnit } from "./UnitofMeasurementlogic"; 

export const useAddMaterialLogic = ({ handleClose, setLoading, open, selectedMaterial, prefilledMaterialTypeID  }) => {
  //#region Permissions
  const { InventoryPermissionProps } = useInventoryPermission();
  const {
    // Material Permissions
    hasMaterialSavePermission,

    // Asset & Material Type Permissions
    hasTypeCatalogGetPermission,
  } = InventoryPermissionProps;
  //#endregion Permissions

  // Form state
  const [materialForm, setMaterialForm] = useState({
    materialID: 0,
    itemName: "",
    materialTypeID: "",
    description: "",
    unitID: 0,
    costPerUnit: "",
    perUnitPrice: "",
    latestRestockDate: null,
    quantityInStock: "",
    lowStockAlert: "",
    location: "",
    salesTax: "",
    chargeSalesTax: false,
    vendors: "",
    isActive: true
  });

  // Material types for dropdown
  const [materialTypes, setMaterialTypes] = useState([]);
    const [units, setUnits] = useState([]); 
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);

  // Date constraints
  const today = dayjs();

  // Fetch material types when dialog opens
  useEffect(() => {
    if (open) {
      async function fetchData() {
        if(hasTypeCatalogGetPermission){
          const typesResponse = await GetTypeCatalog("Material");
          if (!typesResponse.isError && typesResponse.result) {
            setMaterialTypes(typesResponse.result);
          } else {
            console.error("Failed to load material types:", typesResponse.message);
          }
        }

        // Fetch units
        await fetchUnitsData();
      }
      
      fetchData();

      if (selectedMaterial) {
        // When editing, prepopulate the material form.
        const materialTypeId = selectedMaterial.materialTypeID ? Number(selectedMaterial.materialTypeID) : "";
        setMaterialForm({
          ...selectedMaterial,
          materialTypeID: materialTypeId,
          latestRestockDate: selectedMaterial.latestRestockDate ? dayjs(selectedMaterial.latestRestockDate) : null,
        });
      }
    }
  }, [open, selectedMaterial]);

  // Separate effect to handle prefilled material type after materialTypes are loaded
  useEffect(() => {
    if (open && prefilledMaterialTypeID && materialTypes.length > 0 && !selectedMaterial) {
      const prefilledType = materialTypes.find(type => Number(type.typeCatalogID) === Number(prefilledMaterialTypeID));
      if (prefilledType) {
        setMaterialForm((prevForm) => ({
          ...prevForm,
          materialTypeID: Number(prefilledMaterialTypeID),
        }));
      }
    }
  }, [open, prefilledMaterialTypeID, materialTypes, selectedMaterial]);

const fetchUnitsData = async () => {
  try {
    const unitsResponse = await getUnits();
    if (!unitsResponse.isError && unitsResponse.result) {
      const formattedUnits = unitsResponse.result.map(unit => {
        const symbol = unit.unitSymbol || ''; 
        const unitName = unit.unitName || '';
        const unitID = unit.unitID;
        
        let label = '';
        
        label = `${unitName}${symbol ? ` (${symbol})` : ''}`
        
        return {
          value: unitID,
          label: label,
          originalData: unit
        };
      });
      setUnits(formattedUnits);
    } else {
      console.error("Failed to load units:", unitsResponse.message);
    //   toast.error("Failed to load units", {
    //   style: {
    //     fontFamily: '"DM Sans", sans-serif',
    //     fontSize: '15px',
    //   },
    // });
    }
  } catch (error) {
    console.error("Error fetching units:", error);
    toast.error("Error loading units", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
  }
};
  const fetchMaterialTypes = async () => {
    try {
      setLoading(true);
      const response = await GetTypeCatalog("Material");
      if (!response.isError && response.result) {
        const materialTypesWithTimestamp = response.result.map(type => ({
          ...type,
          _timestamp: Date.now()
        }));
        setMaterialTypes(materialTypesWithTimestamp);
      } else {
        console.error("Error fetching material types:", response.message);
    //     toast.error("Failed to load material types", {
    //   style: {
    //     fontFamily: '"DM Sans", sans-serif',
    //     fontSize: '15px',
    //   },
    // });
      }
    } catch (error) {
      console.error("Error fetching material types:", error);
    //   toast.error("Failed to load material types", {
    //   style: {
    //     fontFamily: '"DM Sans", sans-serif',
    //     fontSize: '15px',
    //   },
    // });
    } finally {
      setLoading(false);
    }
  };

  const handleUnitCreated = async (newUnit) => {
    console.log("Unit created callback:", newUnit);  
    await fetchUnitsData();
    if (newUnit && newUnit.result) {
      const createdUnit = newUnit.result;
      const unitValue = createdUnit.unitSymbol || createdUnit.unitName;
      console.log("Auto-selecting unit:", unitValue, "from:", createdUnit);
      
      if (unitValue) {
        setMaterialForm(prevForm => ({
          ...prevForm,
          unitID: createdUnit.unitID
        }));
      }
    }
  };
  const handleInputChange = (e) => {
    if (!hasMaterialSavePermission) return;
    const { name, value } = e.target;
    const numericFields = [
      "hourlyRate",
      "purchasePrice",
      "operationalCostsAmount",
      "cost",
      "price",
      "amount"
    ];

    if (numericFields.includes(name)) {
      if (value === '' || value === '.' || value === '0.' || /^\d*\.?\d*$/.test(value)) {
        setMaterialForm(prevForm => ({
          ...prevForm,
          [name]: value.replace(/,/g, ''),
        }));
      }
    } else {
      setMaterialForm(prevForm => ({
        ...prevForm,
        [name]: value,
      }));
    }
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: null,
      }));
    }
  };

  const removeCommas = (value) => value.replace(/,/g, '');
  const handleNumberInputChange = (e) => {
    if (!hasMaterialSavePermission) return;

    const { name, value } = e.target;
    if (value === "") {
      setMaterialForm(prev => ({ ...prev, [name]: "" }));
      return;
    }

    const rawValue = removeCommas(value);
    const isNegativeField = name === 'quantityInStock';

    // Define validation patterns
    let isValid = false;
    let cleanValue = rawValue;

    if (isNegativeField) {
      // Allow: empty, standalone '-', or valid negative numbers
      isValid = rawValue === '-' || /^-?(\d+\.?\d*|\.\d*)$/.test(rawValue);
    } else {
      // Allow only positive numbers and decimals
      isValid = /^(\d+\.?\d*|\.\d*)$/.test(rawValue);
    }

    if (isValid) {
      setMaterialForm(prev => ({ ...prev, [name]: cleanValue }));

      // Clear error if any
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: null }));
      }
    }
  };

  const handleDateChange = (name, value) => {
    if(!hasMaterialSavePermission) return;

    // console.log(`Date change: ${name} = ${value}`); // Add debug logging
    setMaterialForm(prevForm => ({
      ...prevForm,
      [name]: value
    }));
    
    // Clear error for this field if any
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    if(!hasMaterialSavePermission) return;

    const { name, checked } = e.target;
    // console.log(`Checkbox change: ${name} = ${checked}`); // Add debug logging
    setMaterialForm(prevForm => ({
      ...prevForm,
      [name]: checked
    }));
  };

  const handleStatusChange = (e) => {
    if(!hasMaterialSavePermission) return;

    const { value } = e.target;
    // console.log(`Status change: ${value}`); // Add debug logging
    setMaterialForm(prevForm => ({
      ...prevForm,
      isActive: value === "Available"
    }));
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!materialForm.itemName.trim()) {
      newErrors.itemName = "Material Name is required";
    }
    
    if (!materialForm.materialTypeID) {
      newErrors.materialTypeID = "Material Type is required";
    }

    if (!materialForm.unitID) {
      newErrors.unitOfMeasurement = "Unit of Measure is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save material
  const handleSaveMaterial = async () => {
    if(!hasMaterialSavePermission) return { success: false };

    try {
      if (!validateForm()) {
        return { success: false };
      }

      setLoading(true);

      // Format data for API
      const formattedMaterial = {
        ...materialForm,
        costPerUnit: materialForm.costPerUnit ? Number(materialForm.costPerUnit) : 0,
        perUnitPrice: materialForm.perUnitPrice ? Number(materialForm.perUnitPrice) : 0,
        quantityInStock: materialForm.quantityInStock ? Number(materialForm.quantityInStock) : 0,
        lowStockAlert: materialForm.lowStockAlert ? Number(materialForm.lowStockAlert) : 0,
        salesTax: materialForm.salesTax ? Number(materialForm.salesTax) : 0,
        latestRestockDate: materialForm.latestRestockDate 
          ? formatDateForMySQL(materialForm.latestRestockDate.toDate()) 
          : null,
      };

      const response = await SaveMaterial(formattedMaterial);

      if (!response.isError) {
        // If we have a file to upload and the material was created successfully
        if (selectedFile && response.result?.materialID) {
          const uploadResponse = await uploadMaterialAttachment(response.result.materialID, selectedFile);
          if (uploadResponse.isError) {
            toast.error(uploadResponse.message || "Failed to upload attachment", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
          } else {
            toast.success("Material and attachment created successfully", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
          }
        } else {
          toast.success(materialForm.materialID ? "Material updated successfully" : "Material created successfully", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        }
        
        // Prepare the created/updated item data
        const createdMaterialID = response.result?.materialID || materialForm.materialID;
        const itemData = {
          ...response.result, // Spread response first so our values override it
          materialID: createdMaterialID,
          itemName: materialForm.itemName,
          materialTypeID: materialForm.materialTypeID,
          unitID: materialForm.unitID,
          costPerUnit: materialForm.costPerUnit,
          perUnitPrice: materialForm.perUnitPrice,
          quantityInStock: materialForm.quantityInStock,
        };
        
        handleClose();
        return { success: true, data: itemData };
      } else {
        toast.error(response.message || "Failed to save material", {
          style: {
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '15px',
          },
        });
        return { success: false };
      }
    } catch (error) {
      toast.error("An error occurred while saving the material", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
      console.error("Error saving material:", error);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (file) => {
    if(!hasMaterialSavePermission) return;

    setSelectedFile(file);
  };

  const resetForm = () => {
    setMaterialForm({
      materialID: 0,
      itemName: "",
      materialTypeID: "",
      description: "",
      unitID: "",
      costPerUnit: "",
      perUnitPrice: "",
      latestRestockDate: null,
      quantityInStock: "",
      lowStockAlert: "",
      location: "",
      salesTax: "",
      chargeSalesTax: false,
      vendors: "",
      isActive: true
    });
    setErrors({});
    setSelectedFile(null);
  };

  return {
    materialForm,
    materialTypes,
    units,
    errors,
    today,
    handleInputChange,
    handleNumberInputChange,
    handleDateChange,
    handleCheckboxChange,
    handleStatusChange,
    handleSaveMaterial,
    resetForm,
    handleFileChange,
    selectedFile,
    setSelectedFile,
     fetchUnitsData, 
    handleUnitCreated
  };
}; 